import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, makeObservable, observable } from 'mobx';

import { GetCertificateCollaboration } from '@/application/collaborations/certificate/get-certificate.collaboration';
import type { ClassifierDto } from '@/application/interfaces/pegasus.gateway';
import { shareWithLinkedin } from '@/application/utils/pdf-make/share-certificate';
import { Tracking } from '@/application/utils/tracking';
import envs from '@/infrastructure/config/envs';
import { MicrofrontendViewModel } from '@/presentation/Microfrontend.vm';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

export interface Certificate {
  guid?: string;
  topicName?: string;
  studentName?: string;
  workload?: number;
  completeIn?: Date;
  expirationIn?: Date;
  certificateSettings?: {
    id?: string;
    logoImageUrl?: string;
    logoPartnerImageUrl?: string;
    assignImageUrl?: string;
    assignPartnerImageUrl?: string;
    backgroundImageUrl: string;
    minimumProgressPercentage?: string;
    courseApprovalPercentage?: string;
  };
}

@ViewModel({ singleton: true })
export class CertificateViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable classifiers: ClassifierDto[] = [];
  @observable highestClassificationLevels: ClassifierDto[] = [];
  @observable certificate: Certificate;
  @observable linkedinUrl = '';

  constructor(
    private getCertificateCollaboration: GetCertificateCollaboration,
    private readonly microfrontendViewModel: MicrofrontendViewModel,
    private readonly studentClassesStore: StudentClassesStore,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    if (!this.microfrontendViewModel.user) {
      return;
    }
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());
    this.isLoading = false;
  }

  @action
  async handleScreenCertificate(courseGuid: string) {
    this.isLoading = true;
    try {
      const { data } = await this.getCertificateCollaboration.run(courseGuid);

      this.certificate = {
        guid: data.guid,
        topicName: data.course,
        studentName: data.student,
        workload: data.workload,
        completeIn: data.issueDate,
        expirationIn: data.expirationDate,
        certificateSettings: {
          backgroundImageUrl: data.background,
        },
      };
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action
  setClassifiers(classifiers: ClassifierDto[]) {
    this.classifiers = classifiers;
  }

  @action
  setLinkedinUrl(publicUrl: string) {
    const { topicName, guid, completeIn, expirationIn } = this.certificate;

    const requestURL = shareWithLinkedin(
      topicName,
      publicUrl,
      envs.linkedinOrganizationId,
      guid,
      completeIn,
      expirationIn,
    );

    this.linkedinUrl = requestURL;
  }
}
